/**
 * Security — public marketing page.
 *
 * Speaks to two audiences:
 *   1. Operators (CEOs, CIOs, family-office principals) who need to
 *      satisfy themselves and their boards that putting deal data and
 *      trade secrets into Vigilant Entities is *safer* than the
 *      alternative (a generic AI chatbot or a private spreadsheet).
 *   2. Regulators / auditors who'll ask "where does the data go and
 *      who could read it?"
 *
 * Structure:
 *   Hero with five trust badges
 *   Section 1 — Sovereign AI: prompts, models, models, model output
 *   Section 2 — Encryption at every layer
 *   Section 3 — PII + trade-secret redaction
 *   Section 4 — Tenant isolation (data plane, identity plane)
 *   Section 5 — Audit + Sovereignty Score
 *   Section 6 — Provider policy matrix
 *   Section 7 — Compliance posture + FAQ
 *
 * Tone matches /modules and EcosystemGateway: Bloomberg-terminal /
 * Palantir-deep, with Sovereign Violet + Chrome accents.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Lock, Cloud, KeyRound, Network, Eye, FileText, Activity,
  ArrowRight, CheckCircle2, AlertTriangle, Sparkles, ShieldAlert, Clock,
  Radio,
} from 'lucide-react';
import LivingBackground from '@/components/LivingBackground';
import PrivacyGlanceWidget from '@/components/PrivacyGlanceWidget';
import PublicNavbar from '@/components/PublicNavbar';
import AuditIntegrityBadge from '@/components/AuditIntegrityBadge';
import AuditIntegrityPill from '@/components/AuditIntegrityPill';
import api from '@/lib/api';
import CountUp from '@/components/CountUp';

const TRUST_PILLARS = [
  { icon: Cloud,        label: 'Sovereign AI',          sub: 'Bedrock (AWS) or Cosmos DB (Azure)' },
  { icon: Lock,         label: 'Encryption everywhere', sub: 'TLS 1.3 ingress · field-level at rest' },
  { icon: Eye,          label: 'Auto-redaction',        sub: 'PII + trade-secret tokens stripped pre-send' },
  { icon: Network,      label: 'Tenant isolation',      sub: 'Row-scoped Mongo · scoped IAM roles' },
  { icon: Activity,     label: 'Sovereignty Score',     sub: 'Provable per-call audit log' },
  { icon: ShieldAlert,  label: 'Brute-force shield',    sub: 'Per-email + per-IP rate limit on login' },
];

const PROVIDERS = [
  { id: 'bedrock',   label: 'Amazon Bedrock (your AWS)',           training: 'Never',         verdict: 'gold', note: 'LLM compute inside YOUR AWS region. Prompts + responses never leave your account.' },
  { id: 'azure_cosmos', label: 'Microsoft Azure Cosmos DB (your Azure)', training: 'Never',    verdict: 'gold', note: 'Tenant application data hosted inside YOUR Azure subscription (Cosmos DB for MongoDB — vCore / RU — or MongoDB on Azure VM/AKS). Vigilant has no standing credentials to it.' },
  { id: 'anthropic', label: 'Anthropic Claude',                    training: 'Opted-out',     verdict: 'ok',   note: 'API traffic contractually never used for training. 30-day abuse retention.' },
  { id: 'openai',    label: 'OpenAI GPT',                          training: 'Opted-out',     verdict: 'ok',   note: 'API traffic opted out by default since Mar 2023. 30-day abuse retention.' },
  { id: 'gemini',    label: 'Google Gemini',                       training: 'Opted-out',     verdict: 'ok',   note: 'Paid-tier traffic never used for training (Vertex AI policy).' },
  { id: 've_gateway', label: 'VE LLM Gateway',                     training: 'Pass-through',  verdict: 'ok',   note: 'TLS-only routing. VE Gateway does not retain prompts.' },
];

const FAQ = [
  { q: 'Do my AI prompts train someone else\u2019s model?', a: 'No. Every external provider we route to (Anthropic, OpenAI, Gemini) is on the API contract that explicitly opts traffic out of training. When you connect your own AWS account and enable Bedrock, prompts + responses never leave your AWS region at all — even we cannot see them.' },
  { q: 'Where is my data physically stored?', a: 'Two planes, both operator-controlled: (1) Application data — either in our managed MongoDB cluster (encrypted at rest via field-level Fernet + KMS) in the region you choose at sign-up, or in YOUR own Microsoft Azure Cosmos DB (vCore, RU, or MongoDB on Azure VM/AKS) once you paste your connection string at /dashboard/admin/azure or /dashboard/settings/azure. (2) Files + AI workloads — in your own AWS S3 / Bedrock once you connect AWS, otherwise in our managed buckets. Bring-your-own AWS OR Azure OR both — sovereign tenancy per plane.' },
  { q: 'What is the Sovereignty Score?', a: 'A live KPI on your AWS Connection page: % of LLM calls in the last 30 days that ran inside your AWS region (Bedrock) versus the shared VE LLM Gateway fallback. Auditors love it. It is regenerated every time you load the page. Data-plane sovereignty (tenant DB in your own Azure Cosmos) surfaces as a separate "Azure connected" badge on the Azure Connection page.' },
  { q: 'What happens to my trade-secret codenames?', a: 'Add them to the Sovereign-AI Privacy Center. Every external LLM call passes through a regex redactor that replaces those tokens with [redacted-secret] before the prompt leaves the cluster. You can also disable individual built-in PII patterns if your workflow needs them intact.' },
  { q: 'Can other tenants see my data?', a: 'No. Every Mongo query is scoped by `tenant_id` server-side via a single `with_tenant()` helper that all 80+ routes share. Cross-tenant reads require the system_admin role and are logged. White-label tenants run in fully isolated rooms.' },
  { q: 'Do you protect against credential-stuffing or brute-force attacks?', a: 'Yes. Every failed login is logged with a sliding window — 5 failed attempts for the same email in 15 minutes triggers a 15-minute lockout, and 10 failed attempts from the same source IP triggers the same. Locked sessions get HTTP 429 with a Retry-After header (your client should respect it). The lockout check runs BEFORE bcrypt compare so an attacker hitting the limit never gets timing signal about whether the email exists. Admins see active lockouts on the Security dashboard and can clear them with one click. We do not use CAPTCHAs — this is a pure server-side mitigation.' },
  { q: 'What encryption do you use?', a: 'TLS 1.3 between the user and our ingress. TLS 1.2+ between our cluster and every external LLM provider (HTTPS over boto3 + the VE LLM Gateway SDK). At rest: per-document Fernet (AES-256) for sensitive fields, with the master key in a KMS-managed envelope. When you paste a CMK ARN on the Admin → Field Encryption page, every new write is wrapped with your CMK from then on.' },
];


export default function SecurityPage() {
  return (
    <div className="min-h-screen relative bg-[var(--ve-bg)] text-[var(--ve-fg)]">
      <LivingBackground />
      <PublicNavbar />
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-12 lg:py-20" data-testid="security-page">

        {/* Hero */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <div className="ve-eyebrow text-emerald-400">[SECURITY · SOVEREIGN AI · ZERO-LEAK ARCHITECTURE]</div>
            <h1 className="display text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Your AI works for <span style={{ color: 'var(--brand-primary)' }}>you</span>.<br/>
              Your data stays with <span className="text-emerald-300">you</span>.
            </h1>
            <p className="mt-6 text-base lg:text-lg opacity-75 leading-relaxed max-w-xl">
              Vigilant Entities was built on a single rule: when your AI advisor reads your deal flow, your founder names, your IBANs and your project codenames — none of that should ever leak to a public training set. Not now, not ever.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" data-testid="security-cta-login" className="ve-btn-primary inline-flex items-center gap-1.5">
                See it inside <ArrowRight size={13}/>
              </Link>
              <Link to="/modules-full" data-testid="security-cta-modules" className="px-5 py-2.5 rounded border border-white/20 text-sm font-bold uppercase tracking-[0.18em] hover:bg-white/5 transition-colors">
                Explore modules
              </Link>
            </div>
          </div>
          {/* Trust badge grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {TRUST_PILLARS.map((p) => (
              <div key={p.label} className="p-4 rounded-lg border border-emerald-400/20 bg-emerald-500/5 backdrop-blur" data-testid={`trust-${p.label.toLowerCase().replace(/\s/g, '-')}`}>
                <p.icon size={20} className="text-emerald-300 mb-2"/>
                <div className="text-[12px] font-bold uppercase tracking-[0.12em]">{p.label}</div>
                <div className="text-[10px] opacity-65 mt-1 leading-relaxed">{p.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Audit-integrity live badge — fed by /api/security/audit/verify/public */}
        <section className="mb-20">
          <AuditIntegrityBadge />
        </section>

        {/* Section 1 — Sovereign AI */}
        <Section eyebrow="[01]" title="Sovereign AI" subtitle="Where every prompt actually goes.">
          <div className="mb-6">
            <PrivacyGlanceWidget />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Step
              n="1" title="Inside our cluster"
              body="The query enters our FastAPI cluster via TLS 1.3. We never log the body. The Sovereign-AI Redactor strips PII + your configured trade-secret codenames here, before anything else happens."
            />
            <Step
              n="2" title="Routed by tenant choice"
              body="Two independent sovereignty planes, each opt-in. LLM compute: if you have connected your own AWS and enabled Amazon Bedrock, every prompt + response runs entirely inside YOUR AWS region — otherwise we route via the VE LLM Gateway to Anthropic / OpenAI / Gemini (all opted out of training). Data plane: if you have connected Microsoft Azure Cosmos DB, all tenant application data is written to YOUR Azure subscription. Bring-your-own AWS, Azure, or both."
            />
            <Step
              n="3" title="Receipted forever"
              body="Every LLM call records a routing receipt — provider, model, region, latency, and whether redactor fired. Auditors can prove every call in the last 90 days stayed inside your account, with one click."
            />
          </div>
        </Section>

        {/* Section 2 — Encryption layers */}
        <Section eyebrow="[02]" title="Encryption at every layer" subtitle="At rest, in transit, in motion.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Row icon={Lock}        label="TLS 1.3 ingress"            value="Kubernetes ingress, cluster-terminated. No HTTP downgrade allowed."/>
            <Row icon={Network}     label="TLS 1.2+ outbound to LLMs"  value="boto3 (Bedrock) + the VE LLM Gateway SDK both enforce HTTPS. We bind to provider hostnames, never IPs."/>
            <Row icon={KeyRound}    label="Field-level Fernet (AES-256)" value="Sensitive document fields (notes, financials, founder PII) are envelope-encrypted with per-document keys."/>
            <Row icon={ShieldCheck} label="KMS / CMK wrapping"          value="Paste your AWS KMS CMK ARN on the Field Encryption page. From then on, every new write is wrapped by your customer key."/>
            <Row icon={Sparkles}    label="Mongo Atlas at-rest"         value="EBS-level AES-256 at the storage layer. Atlas KMS integration available on the Admin → Field Encryption page."/>
            <Row icon={FileText}    label="Backups"                     value="Daily mongodump compressed + encrypted to Atlas-managed buckets. Retention 30 days, encrypted in transit + at rest."/>
          </div>
        </Section>

        {/* Section 3 — PII + Trade-secret redaction */}
        <Section eyebrow="[03]" title="PII + Trade-secret redaction" subtitle="Built into every LLM call. On by default.">
          <p className="mb-6 text-sm opacity-75 max-w-3xl leading-relaxed">
            We don&apos;t trust that &ldquo;the model will probably not remember.&rdquo; We strip the secrets before they ever reach the model.
            Nine built-in PII patterns + unlimited tenant-defined trade-secret tokens are scrubbed from every outbound prompt.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {['Email', 'Phone', 'URL', 'Social handle', 'Qatari ID', 'IBAN', 'US SSN', 'Credit card', 'Passport', 'Trade-secret tokens'].map((p) => (
              <div key={p} className="px-3 py-2 rounded border border-emerald-400/30 bg-emerald-500/5 text-[11px] pal-mono">
                <CheckCircle2 size={10} className="inline mr-1 text-emerald-300"/> {p}
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 rounded border border-amber-400/30 bg-amber-500/5 text-[12px] leading-relaxed">
            <AlertTriangle size={13} className="inline mr-1 text-amber-300"/>
            <strong>Override-able.</strong> An admin can toggle a specific pattern OFF (e.g. URLs) when the workflow needs that data intact. Trade-secret tokens cannot be disabled.
          </div>
        </Section>

        {/* Section 4 — Tenant isolation */}
        <Section eyebrow="[04]" title="Tenant isolation" subtitle="One Mongo, many sovereign rooms.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card title="Data plane" body="Every Mongo query is wrapped by a single `with_tenant()` helper that scopes the filter to the caller's `tenant_id` server-side. Cross-tenant reads require system_admin role and are logged in the audit trail."/>
            <Card title="Identity plane" body="Roles (system_admin, holding_admin, incubator_admin, founder, investor, government, guest) gate every route via FastAPI dependencies. JWT tokens carry only `user_id` + signed claims — never PII."/>
            <Card title="AWS plane (BYO LLM compute)" body="Tenants who connect AWS get their own STS-assumed IAM role with external-id protection. Bedrock + KMS + S3 traffic for that tenant routes through their role only — we have no standing credentials."/>
            <Card title="Azure plane (BYO tenant DB)" body="Tenants who connect Microsoft Azure paste an encrypted Cosmos DB (or MongoDB-on-Azure) connection string at /dashboard/admin/azure. All application writes go to their Azure subscription; Vigilant retains no standing access to that database."/>
            <Card title="Privacy plane" body="Each tenant has their own `privacy_secrets` (codenames to redact) and `privacy_pattern_overrides` (PII patterns enabled/disabled). Their settings affect only their own outbound LLM calls."/>
          </div>
        </Section>

        {/* Section 5 — Audit trail */}
        <Section eyebrow="[05]" title="Audit + Sovereignty Score" subtitle="Numbers your auditor can sign.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card title="Council Routing Receipt" body="Every AI Council session records a per-persona log: provider, model, region, latency_ms, ok/fail. Shows 'all_bedrock=true' badge when every call stayed inside your AWS."/>
            <Card title="Sovereignty Score (30d)" body="A single percentage showing what fraction of LLM calls ran inside your own AWS region. 30-day trend sparkline. Visible on the AWS Connection page hero."/>
            <Card title="Redaction Audit Feed" body="Last 50 redaction events (counts only, never the raw text). Lets you prove the redactor fired, day after day, without ever exposing the masked PII to auditors."/>
          </div>
        </Section>

        {/* Section 6 — Brute-force shield (live KPI) */}
        <Section
          eyebrow="[06]"
          title="Brute-force shield"
          subtitle="Per-email + per-IP rate limiting. On by default, every tenant."
        >
          <BruteForcePanel />
        </Section>

        {/* Section 7 — Provider policy matrix */}
        <Section eyebrow="[07]" title="Provider policy matrix" subtitle="Who could theoretically see what.">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-60">
                  <th className="text-left py-2 font-bold">Provider</th>
                  <th className="text-left py-2 font-bold">Training use</th>
                  <th className="text-right py-2 font-bold">Verdict</th>
                  <th className="text-left py-2 pl-6 font-bold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {PROVIDERS.map((p) => (
                  <tr key={p.id} data-testid={`security-provider-${p.id}`} className="border-t border-white/5">
                    <td className="py-3 font-bold">{p.label}</td>
                    <td className="py-3 pal-mono text-xs">{p.training}</td>
                    <td className="py-3 text-right">
                      <span className={`pal-mono text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded border ${p.verdict === 'gold' ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-300' : 'border-[var(--brand-primary-strong)] bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}>
                        {p.verdict}
                      </span>
                    </td>
                    <td className="py-3 pl-6 text-xs opacity-75 max-w-[480px]">{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Section 8 — Compliance alignment */}
        <Section
          eyebrow="[08]"
          title="Aligned with international standards"
          subtitle="The frameworks your auditor will ask about — and how we map to them."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="security-compliance-grid">
            {[
              {
                id: 'owasp',
                code: 'OWASP',
                title: 'OWASP ASVS + Top 10',
                body: 'Full coverage matrix in SECURITY.md. Strict CSP, HSTS preload, anti-CSRF, brute-force lockouts, per-device JWT, hash-chained audit log.',
              },
              {
                id: 'iso27001',
                code: 'ISO/IEC',
                title: 'ISO 27001 (Annex A)',
                body: 'Controls mapped clause-by-clause: A.5 policies, A.9 access control, A.10 cryptography, A.12 logging, A.13 network security, A.15 supplier risk, A.16 incident response.',
              },
              {
                id: 'soc2',
                code: 'SOC 2',
                title: 'SOC 2 Type II readiness',
                body: 'Trust Service Criteria CC1, CC6, CC7, CC8, CC9 all addressed. Audit trail, change management, vendor risk register, incident response playbook in place.',
              },
              {
                id: 'nist',
                code: 'NIST',
                title: 'NIST 800-53 (Moderate)',
                body: 'AC (access), AU (audit), IA (identification), SC (system & comms), SI (system integrity) families. AU-9 tamper-evident logs verifiable live above.',
              },
              {
                id: 'gdpr',
                code: 'GDPR',
                title: 'GDPR Articles 15 / 17 / 20 / 32 / 33',
                body: 'Right to access + export (Art. 15/20) and right to erasure (Art. 17) available from your Profile page. Breach notification process documented for 72-hour window.',
              },
              {
                id: 'nist63b',
                code: 'NIST',
                title: 'NIST 800-63B passwords',
                body: '12+ character minimum, blocklist against known breaches, no forced rotation, MFA-like passwordless magic-links. NIST-aligned, not 1990s-aligned.',
              },
            ].map((c) => (
              <div
                key={c.id}
                data-testid={`compliance-card-${c.id}`}
                className="rounded-lg border p-5 backdrop-blur transition-colors"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(174,82,234,0.22)',
                }}
              >
                <div
                  className="text-[9px] uppercase tracking-[0.28em] font-bold mb-2"
                  style={{ color: '#D4AF37' }}
                >
                  [{c.code}]
                </div>
                <div className="font-bold text-sm leading-tight">{c.title}</div>
                <p className="mt-2 text-[12px] opacity-70 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[11px] opacity-55 leading-relaxed max-w-3xl">
            <span className="font-bold opacity-80">Note on certifications:</span>{' '}
            Vigilant Entities is built on these standards and ships with the
            documented evidence package (SECURITY.md, THREAT_MODEL.md,
            RBAC_MATRIX.md, INCIDENT_RESPONSE.md, BACKUP_RUNBOOK.md,
            VENDOR_RISK.md, CycloneDX SBOM). Formal certification audits for
            ISO 27001 and SOC 2 Type II are scheduled with our compliance
            partner — request status from{' '}
            <a href="mailto:security@vigilantentities.com" className="underline opacity-90 hover:opacity-100">security@vigilantentities.com</a>.
          </p>
        </Section>

        {/* Section 9 — FAQ */}
        <Section eyebrow="[09]" title="Frequently asked" subtitle="The questions auditors and CIOs actually ask us.">
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="ve-card p-5 group cursor-pointer" data-testid={`security-faq-${i}`}>
                <summary className="font-bold text-base flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <span className="opacity-50 group-open:rotate-90 transition-transform">›</span>
                </summary>
                <p className="mt-3 text-sm opacity-80 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <div className="mt-20 ve-card p-8 lg:p-12 text-center">
          <h2 className="display text-3xl lg:text-4xl">Trust, but verify.</h2>
          <p className="mt-3 opacity-75 max-w-2xl mx-auto">Login to your tenant, paste an IBAN into the Privacy Sandbox, and watch the redactor strip it before any prompt leaves the cluster. Then check your Sovereignty Score — and watch it climb as you migrate workloads to your own AWS.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/login" data-testid="security-cta-bottom-login" className="ve-btn-primary inline-flex items-center gap-1.5">Login & verify <ArrowRight size={13}/></Link>
            <Link to="/pricing" data-testid="security-cta-bottom-pricing" className="px-5 py-2.5 rounded border border-white/20 text-sm font-bold uppercase tracking-[0.18em] hover:bg-white/5 transition-colors">See pricing</Link>
          </div>
        </div>

        <footer className="mt-16 pt-8 border-t border-white/5 flex items-center justify-between flex-wrap gap-3 text-[10px] uppercase tracking-[0.18em]">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="opacity-50">© Vigilant Entities · M3 Holdings · Doha</span>
            <span className="opacity-100"><AuditIntegrityPill /></span>
          </div>
          <div className="flex gap-4 opacity-50">
            <Link to="/modules-full" className="hover:opacity-80">Modules</Link>
            <Link to="/pricing"      className="hover:opacity-80">Pricing</Link>
            <Link to="/security"     className="hover:opacity-80">Security</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Section({ eyebrow, title, subtitle, children }) {
  return (
    <section className="mb-16">
      <div className="mb-6">
        <div className="ve-eyebrow">{eyebrow}</div>
        <h2 className="display text-3xl lg:text-4xl mt-2">{title}</h2>
        {subtitle && <p className="text-sm opacity-65 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="ve-card p-5">
      <div className="pal-mono text-xs font-bold opacity-50">STEP {n}</div>
      <div className="text-base font-bold mt-1">{title}</div>
      <p className="text-sm opacity-75 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 ve-card p-4">
      <Icon size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0"/>
      <div>
        <div className="font-bold text-sm">{label}</div>
        <div className="text-xs opacity-70 mt-1 leading-relaxed">{value}</div>
      </div>
    </div>
  );
}

function Card({ title, body }) {
  return (
    <div className="ve-card p-5">
      <div className="font-bold text-base">{title}</div>
      <p className="text-sm opacity-75 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function BruteForcePanel() {
  const [kpi, setKpi] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancel = false;
    api.get('/auth/security/brute-force-public')
      .then(({ data }) => { if (!cancel) setKpi(data); })
      .catch(() => {/* public endpoint should never fail, but degrade gracefully */});
    return () => { cancel = true; };
  }, [tick]);

  // Auto-refresh every 30s so the live feed actually feels live.
  // 30s is gentle enough not to hammer the endpoint on a public page.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const failed24h = kpi?.failed_logins_last_24h ?? '—';
  const active = kpi?.active_lockouts ?? '—';
  const emailMax = kpi?.policy?.email_max_attempts ?? 5;
  const ipMax = kpi?.policy?.ip_max_attempts ?? 10;
  const lockMin = kpi?.policy?.lockout_minutes ?? 15;
  const windowMin = kpi?.policy?.window_minutes ?? 15;

  return (
    <>
      <p className="mb-6 text-sm opacity-75 max-w-3xl leading-relaxed">
        Every failed login is logged with a sliding window — both the target
        email and the source IP. Cross the threshold and the gate slams shut
        with <span className="pal-mono text-[var(--brand-primary)]">HTTP 429 · Retry-After</span> before
        any password compare runs (so attackers can&apos;t even use timing to
        confirm whether an email exists). Auto-unlocks after the window;
        admins can clear early.
      </p>

      {/* Lifetime hero counter — psychologically strong number that grows
          monotonically. Pulls from /auth/security/brute-force-public.lifetime. */}
      <LifetimeSavedHero kpi={kpi} />

      {/* Live KPI strip — three counters fed by /auth/security/brute-force-public */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="ve-card p-4 border-l-2 border-rose-400/50" data-testid="brute-force-failed-24h">
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 font-bold">Failed logins · 24h</div>
          <div className="text-2xl font-bold pal-mono mt-1 text-rose-300">{failed24h}</div>
          <div className="text-[10px] opacity-50 mt-1">Across every tenant.</div>
        </div>
        <div className="ve-card p-4 border-l-2 border-amber-400/50" data-testid="brute-force-active-locks">
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 font-bold">Active lockouts</div>
          <div className="text-2xl font-bold pal-mono mt-1 text-amber-300">{active}</div>
          <div className="text-[10px] opacity-50 mt-1">Email or IP currently throttled.</div>
        </div>
        <div className="ve-card p-4 border-l-2 border-emerald-400/50" data-testid="brute-force-lockout-window">
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-60 font-bold">Lockout window</div>
          <div className="text-2xl font-bold pal-mono mt-1 text-emerald-300">{lockMin} min</div>
          <div className="text-[10px] opacity-50 mt-1">Sliding window: {windowMin} min.</div>
        </div>
      </div>

      {/* Policy detail */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ve-card p-5 flex items-start gap-3">
          <ShieldAlert size={20} className="text-rose-300 shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold text-base">Per-email lockout</div>
            <p className="text-sm opacity-75 mt-1.5 leading-relaxed">
              <span className="pal-mono text-rose-300">{emailMax} failures</span> in a {windowMin}-minute window on the same email triggers
              a {lockMin}-minute lockout. Defends against credential-stuffing on a known account.
              A successful login wipes the counter for that email.
            </p>
          </div>
        </div>
        <div className="ve-card p-5 flex items-start gap-3">
          <Network size={20} className="text-amber-300 shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold text-base">Per-IP lockout</div>
            <p className="text-sm opacity-75 mt-1.5 leading-relaxed">
              <span className="pal-mono text-amber-300">{ipMax} failures</span> from the same source IP in {windowMin} min triggers
              the same {lockMin}-minute lockout. Defends against distributed scans across many emails.
              X-Forwarded-For is respected — works correctly behind ingress / WAF.
            </p>
          </div>
        </div>
        <div className="ve-card p-5 flex items-start gap-3">
          <Clock size={20} className="text-emerald-300 shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold text-base">Forensic visibility</div>
            <p className="text-sm opacity-75 mt-1.5 leading-relaxed">
              Every failed attempt is recorded in <code className="pal-mono opacity-80">login_attempts</code> with timestamp, IP and user-agent,
              auto-pruned after 30 days. Active lockouts appear on the admin Security dashboard, sortable, with one-click clear.
              Lockout events also flow into the immutable audit log.
            </p>
          </div>
        </div>
        <div className="ve-card p-5 flex items-start gap-3">
          <ShieldCheck size={20} className="text-[var(--brand-primary)] shrink-0 mt-0.5"/>
          <div>
            <div className="font-bold text-base">Pre-bcrypt gating</div>
            <p className="text-sm opacity-75 mt-1.5 leading-relaxed">
              The lockout check runs <strong>before</strong> the bcrypt password compare. Locked attackers
              get the same 429 response regardless of whether the email exists — no enumeration via
              timing side-channel, no CPU burned on bcrypt for known-bad traffic.
            </p>
          </div>
        </div>
      </div>

      {/* Live obfuscated throttle feed — trust theatre for security-conscious
          buyers. Identifiers are server-side redacted so visitors see the
          silhouette of real traffic being shielded without any PII leak. */}
      <BruteForceLiveFeed kpi={kpi} />
    </>
  );
}

function LifetimeSavedHero({ kpi }) {
  const blocked = kpi?.lifetime?.attempts_blocked;
  const raised = kpi?.lifetime?.lockouts_raised;
  const since = kpi?.lifetime?.since;
  // Hero number = blocked attempts + the lockouts themselves, since each
  // lockout was triggered by a failed-attempt the shield captured.
  const total = (typeof blocked === 'number' && typeof raised === 'number')
    ? blocked + raised
    : null;
  const sinceLabel = since
    ? new Date(since).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : '—';
  // Suppress the hero if nothing's ever happened — better to render the
  // panel without a brag-number than to show "0".
  if (typeof total !== 'number' || total <= 0) return null;

  return (
    <div
      className="mb-7 relative overflow-hidden rounded-xl border border-[var(--brand-primary)]/30 bg-gradient-to-br from-[var(--brand-primary)]/[0.10] to-transparent p-6"
      data-testid="brute-force-lifetime-hero"
    >
      {/* Subtle backdrop sparkle */}
      <div className="absolute inset-0 pal-grid-bg opacity-30 pointer-events-none"/>
      <div className="relative flex items-start gap-5 flex-wrap">
        <ShieldCheck size={36} className="text-[var(--brand-primary)] shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="pal-mono text-[10px] uppercase tracking-[0.22em] opacity-65 font-bold">
            Brute-force shield · Lifetime
          </div>
          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            <div
              className="text-5xl sm:text-6xl font-bold pal-mono text-[var(--brand-primary)] tabular-nums leading-none"
              data-testid="brute-force-lifetime-total"
            >
              <CountUp end={total} duration={1200} />
            </div>
            <div className="text-sm opacity-70 leading-tight">
              brute-force attempts blocked<br/>
              <span className="opacity-60">since {sinceLabel}</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
            <Stat label="Lockouts raised" value={raised}/>
            <Stat label="Attempts blocked while locked" value={blocked}/>
            <Stat label="Avg blocks / lockout"
                  value={raised ? Math.round(blocked / raised) : 0}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-3 py-2 rounded border border-white/8 bg-white/[0.02]">
      <div className="pal-mono text-[9px] uppercase tracking-[0.18em] opacity-55 font-bold">{label}</div>
      <div className="pal-mono text-base font-bold tabular-nums mt-0.5">
        <CountUp end={typeof value === 'number' ? value : 0} duration={900} />
      </div>
    </div>
  );
}

function BruteForceLiveFeed({ kpi }) {
  const feed = kpi?.live_feed || [];

  return (
    <div className="mt-6 ve-card p-5" data-testid="brute-force-live-feed">
      <div className="flex items-center gap-2 mb-1">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-60"/>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-400"/>
        </span>
        <Radio size={14} className="text-rose-300"/>
        <span className="pal-mono text-[10px] uppercase tracking-[0.22em] font-bold">
          Live · Active Throttles
        </span>
        <span className="ml-auto pal-mono text-[10px] uppercase tracking-[0.18em] opacity-60">
          Identifiers redacted server-side
        </span>
      </div>
      <p className="text-xs opacity-65 mt-1 mb-4 max-w-2xl">
        Real-time feed of currently-locked identifiers. We strip every byte
        that could identify the account or its operator before this leaves
        our cluster — you&apos;re looking at the silhouette of live traffic,
        nothing more.
      </p>

      {feed.length === 0 ? (
        <div className="px-4 py-6 rounded border-dashed border opacity-60 text-center">
          <ShieldCheck size={18} className="inline mb-1 opacity-50"/>
          <div className="pal-mono text-[11px] uppercase tracking-[0.18em] mt-1">
            All clear · no active lockouts right now.
          </div>
          <div className="text-[10px] opacity-60 mt-1">
            Shield is watching — every failed login is logged.
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-12 gap-2 px-3 pal-mono text-[9px] uppercase tracking-[0.18em] opacity-50">
            <div className="col-span-2">Type</div>
            <div className="col-span-6">Identifier (redacted)</div>
            <div className="col-span-2 text-right">Fails</div>
            <div className="col-span-2 text-right">Unlocks in</div>
          </div>
          {feed.map((row, i) => (
            <div
              key={`${row.identifier_type}-${row.identifier_redacted}-${i}`}
              className="grid grid-cols-12 gap-2 px-3 py-2 rounded items-center text-[12px] pal-mono border border-rose-500/15 bg-rose-500/[0.04]"
              data-testid={`brute-force-feed-row-${i}`}
            >
              <div className="col-span-2">
                <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${
                  row.identifier_type === 'email'
                    ? 'bg-rose-500/15 text-rose-300'
                    : 'bg-amber-500/15 text-amber-300'
                }`}>
                  {row.identifier_type === 'email' ? 'EMAIL' : 'IP'}
                </span>
              </div>
              <div className="col-span-6 truncate opacity-90">{row.identifier_redacted}</div>
              <div className="col-span-2 text-right opacity-75 tabular-nums">
                {row.attempts_in_window}
              </div>
              <div className="col-span-2 text-right opacity-75 tabular-nums">
                {row.minutes_remaining}m
              </div>
            </div>
          ))}
          <div className="text-[10px] opacity-50 pal-mono pt-2 leading-relaxed">
            * Showing latest {feed.length} of {kpi?.active_lockouts ?? feed.length} active locks.
            Auto-expires after {kpi?.policy?.lockout_minutes ?? 15}m.
          </div>
        </div>
      )}
    </div>
  );
}
